import { useAtomCallback } from 'jotai/utils';
import { set } from 'lodash-es';
import { useCallback } from 'react';
import { useLoadingBarState } from '../../../../components/LoadingBar/LoadingBarContext';
import { useToastMessageState } from '../../../../components/ToastMessage/ToastMessageProvider';
import { LoadingId } from '../../../../types';
import { getErrorMessage } from '../../../../services/queryHelpers';

import {
  createFieldDataModel,
  createFieldFormConfigDataModel,
  FieldSource,
  getFieldFormatterOverride,
  IField,
} from '../../../../data-models/field2.data-model';
import { RendererType } from '../../../../data-models/field-definition';
import {
  createCustomField,
  deleteCustomField,
  updateCustomField,
} from '../../../../services/queries/MaggieCustomFieldQueries';
import { customFieldsListAtom } from '../../../../services/state/AdminPanel/CustomFieldsStateJ';

export function useCustomFieldsActions() {
  const { actions } = useLoadingBarState();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();

  const deleteField = useAtomCallback(
    useCallback(
      async (get, setAtom, customField: IField<unknown>) => {
        actions.startLoading(LoadingId.deleteCustomField);

        try {
          await deleteCustomField(customField);
          const current = (await get(customFieldsListAtom)) ?? [];
          setAtom(customFieldsListAtom, Promise.resolve(current.filter((d) => d.id !== customField.id)));
          pushSuccessToast({ message: 'Field deleted.' });
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, 'Field deletion failed.');
          pushErrorToast({ message });
        } finally {
          actions.stopLoading(LoadingId.deleteCustomField);
        }
      },
      [actions, pushErrorToast, pushSuccessToast]
    )
  );

  const createField = useAtomCallback(
    useCallback(
      async (get, setAtom, payload: Partial<IField<unknown>>) => {
        actions.startLoading(LoadingId.createCustomField);
        try {
          const res = await createCustomField(prepareCreatePayload(payload));
          const current = (await get(customFieldsListAtom)) ?? [];
          setAtom(customFieldsListAtom, Promise.resolve([...current, res as IField<unknown>]));
          pushSuccessToast({ message: 'Field created.' });
          return res;
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, 'Failed to create field.');
          pushErrorToast({ message });
        } finally {
          actions.stopLoading(LoadingId.createCustomField);
        }
      },
      [actions, pushErrorToast, pushSuccessToast]
    )
  );

  const updateField = useAtomCallback(
    useCallback(
      async (get, setAtom, payload: Partial<IField<unknown>>) => {
        actions.startLoading(LoadingId.updateCustomField);
        try {
          const res = await updateCustomField({
            ...prepareCreatePayload(payload),
            id: payload.id,
          });
          const current = (await get(customFieldsListAtom)) ?? [];
          setAtom(
            customFieldsListAtom,
            Promise.resolve(current.map((field) => (field.id === res.id ? res : field)))
          );

          pushSuccessToast({ message: 'Field updated.' });
          return res;
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, 'Failed to update field.');
          pushErrorToast({ message });
        } finally {
          actions.stopLoading(LoadingId.updateCustomField);
        }
      },
      [actions, pushErrorToast, pushSuccessToast]
    )
  );

  return { deleteField, createField, updateField };
}

export function prepareCreatePayload<T>(payload: Partial<IField<T>>) {
  const result = createFieldDataModel(payload);
  const rendererT = payload.formMeta?.renderer?.type;

  result.systemMeta = {
    source: FieldSource.Foresight,
    formRefs: payload.systemMeta?.formRefs ?? [],
    gridRefs: payload.systemMeta?.gridRefs ?? [],
  };

  // Re-derivation is the fallback, not the rule: a format the admin chose has to survive the
  // payload shaping that used to stamp these ids unconditionally.
  const hasChosenFormat = getFieldFormatterOverride(result) != null;

  if (
    !hasChosenFormat &&
    (rendererT === RendererType.singleSelect || rendererT === RendererType.multiSelect)
  ) {
    result.formMeta = createFieldFormConfigDataModel({
      ...payload.formMeta,
      formatter: {
        id: rendererT === RendererType.multiSelect ? 'stringArray' : 'string',
      },
    });
  }

  if (!hasChosenFormat && rendererT === RendererType.percent) {
    set(result, 'formMeta.formatter.id', 'percent');
    set(result, 'formMeta.formatter.config.style', 'percent');
  }

  if (result.formMeta?.renderer) {
    result.formMeta.renderer.id = rendererT ?? 'text';
  }
  result.type = mapRenderTypeToPrimitive(rendererT ?? RendererType.text);
  return result;
}

export function mapRenderTypeToPrimitive(renderT: string) {
  switch (renderT) {
    case RendererType.currency:
    case RendererType.number:
    case RendererType.percent:
      return 'number';
    case RendererType.multiSelect:
      return 'stringArray';
    case RendererType.singleSelect:
      return 'string';
    default:
      return 'string';
  }
}
